import { useCallback, useEffect, useMemo, useState } from "react";
import {
  CheckCircle2,
  ChevronDown,
  Filter,
  Plus,
  RefreshCw,
  UserCheck,
  Users,
} from "lucide-react";

import {
  ApplicationStatus,
  CandidateApplication,
  Cohort,
  programApi,
} from "../api/program";

const statusLabels: Record<ApplicationStatus, string> = {
  new: "Нова заявка",
  contact_required: "Потрібно зв’язатися",
  interviewed: "Пройшов співбесіду",
  recommended: "Рекомендований",
  approved: "Зарахований",
  reserve: "Резерв",
  rejected: "Відмова",
};

const editableStatuses: Array<Exclude<ApplicationStatus, "approved">> = [
  "new",
  "contact_required",
  "interviewed",
  "recommended",
  "reserve",
  "rejected",
];

const AdminApplicationsPage = () => {
  const [applications, setApplications] = useState<CandidateApplication[]>([]);
  const [cohorts, setCohorts] = useState<Cohort[]>([]);
  const [filter, setFilter] = useState<"" | ApplicationStatus>("");
  const [selectedCohorts, setSelectedCohorts] = useState<
    Record<string, string>
  >({});
  const [comments, setComments] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState("");
  const [message, setMessage] = useState("");
  const [cohortFormOpen, setCohortFormOpen] = useState(false);
  const [cohortForm, setCohortForm] = useState({
    name: "",
    code: "",
    startsAt: "",
    endsAt: "",
    capacity: 20,
  });

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setMessage("");
      const [applicationData, cohortData] = await Promise.all([
        programApi.listApplications(filter || undefined),
        programApi.listCohorts(),
      ]);
      setApplications(applicationData.applications);
      setCohorts(cohortData);
    } catch (error: any) {
      setMessage(
        error.response?.data?.message || "Не вдалося завантажити дані."
      );
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const openCohorts = useMemo(
    () =>
      cohorts.filter((cohort) =>
        ["enrollment", "active"].includes(cohort.status)
      ),
    [cohorts]
  );

  const changeStatus = async (
    application: CandidateApplication,
    status: Exclude<ApplicationStatus, "approved">
  ) => {
    try {
      setBusyId(application._id);
      await programApi.updateApplicationStatus(
        application._id,
        status,
        comments[application._id]
      );
      await loadData();
    } catch (error: any) {
      setMessage(
        error.response?.data?.message || "Не вдалося змінити статус."
      );
    } finally {
      setBusyId("");
    }
  };

  const approve = async (application: CandidateApplication) => {
    const cohortId = selectedCohorts[application._id];
    if (!cohortId) {
      setMessage("Оберіть потік перед підтвердженням заявки.");
      return;
    }

    try {
      setBusyId(application._id);
      await programApi.approveApplication(
        application._id,
        cohortId,
        comments[application._id]
      );
      setMessage("Кандидата зараховано. Календар потоку вже доступний.");
      await loadData();
    } catch (error: any) {
      setMessage(
        error.response?.data?.message || "Не вдалося підтвердити заявку."
      );
    } finally {
      setBusyId("");
    }
  };

  const createCohort = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    try {
      await programApi.createCohort({
        ...cohortForm,
        status: "enrollment",
        startsAt: new Date(cohortForm.startsAt).toISOString(),
        endsAt: new Date(cohortForm.endsAt).toISOString(),
      });
      setCohortFormOpen(false);
      setCohortForm({
        name: "",
        code: "",
        startsAt: "",
        endsAt: "",
        capacity: 20,
      });
      setMessage("Новий потік створено.");
      await loadData();
    } catch (error: any) {
      setMessage(error.response?.data?.message || "Не вдалося створити потік.");
    }
  };

  return (
    <section className="overflow-x-hidden px-3 py-5 sm:px-6 sm:py-7 lg:px-8">
      <div className="mx-auto max-w-[1400px]">
        <div className="mb-6 flex flex-col gap-4 border-b border-[#E0DCD5] pb-5 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p className="text-sm font-black uppercase tracking-[0.14em] text-[#8a6f00]">
              Кабінет адміністратора
            </p>
            <h2 className="mt-1 text-2xl font-bold text-[#29251F] sm:text-3xl">
              Заявки кандидатів
            </h2>
            <p className="mt-2 text-sm text-[#777068]">
              Перегляд, зміна статусу та зарахування кандидата в потік.
            </p>
          </div>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            <button
              type="button"
              onClick={() => setCohortFormOpen((value) => !value)}
              className="inline-flex h-11 items-center justify-center gap-2 rounded-2xl border-2 border-black bg-[#ffd400] px-4 text-sm font-black text-black shadow-[4px_4px_0_#000] transition hover:-translate-y-0.5"
            >
              <Plus size={17} />
              Створити потік
            </button>
            <button
              type="button"
              onClick={loadData}
              className="inline-flex h-10 items-center justify-center gap-2 rounded-lg border border-[#DDD7CF] bg-white px-4 text-sm font-semibold text-[#625C55]"
            >
              <RefreshCw size={16} />
              Оновити
            </button>
          </div>
        </div>

        {message && (
          <div className="mb-5 rounded-lg border border-[#E5D2C5] bg-[#FFF8F3] px-4 py-3 text-sm text-[#8A4A2E]">
            {message}
          </div>
        )}

        {cohortFormOpen && (
          <form
            onSubmit={createCohort}
            className="mb-6 rounded-xl border border-[#E3DED6] bg-white p-4 sm:p-5"
          >
            <div className="mb-4 flex items-center gap-3">
              <Users className="text-[#8a6f00]" size={20} />
              <h3 className="text-lg font-bold text-[#312D28]">Новий потік</h3>
            </div>
            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-5">
              <input
                required
                placeholder="Назва"
                className="h-11 rounded-lg border border-[#DCD7CF] px-3"
                value={cohortForm.name}
                onChange={(event) =>
                  setCohortForm({ ...cohortForm, name: event.target.value })
                }
              />
              <input
                required
                placeholder="Код"
                className="h-11 rounded-lg border border-[#DCD7CF] px-3"
                value={cohortForm.code}
                onChange={(event) =>
                  setCohortForm({ ...cohortForm, code: event.target.value })
                }
              />
              <input
                required
                type="date"
                className="h-11 rounded-lg border border-[#DCD7CF] px-3"
                value={cohortForm.startsAt}
                onChange={(event) =>
                  setCohortForm({ ...cohortForm, startsAt: event.target.value })
                }
              />
              <input
                required
                type="date"
                className="h-11 rounded-lg border border-[#DCD7CF] px-3"
                value={cohortForm.endsAt}
                onChange={(event) =>
                  setCohortForm({ ...cohortForm, endsAt: event.target.value })
                }
              />
              <div className="flex flex-col gap-2 sm:flex-row">
                <input
                  required
                  type="number"
                  min={1}
                  className="h-11 min-w-0 flex-1 rounded-lg border border-[#DCD7CF] px-3"
                  value={cohortForm.capacity}
                  onChange={(event) =>
                    setCohortForm({
                      ...cohortForm,
                      capacity: Number(event.target.value),
                    })
                  }
                />
                <button className="h-11 rounded-lg bg-[#312D28] px-4 text-sm font-semibold text-white">
                  Створити
                </button>
              </div>
            </div>
          </form>
        )}

        <div className="mb-5 flex flex-wrap items-center gap-3">
          <Filter size={17} className="text-[#8A837A]" />
          <div className="relative">
            <select
              value={filter}
              onChange={(event) =>
                setFilter(event.target.value as "" | ApplicationStatus)
              }
              className="h-10 appearance-none rounded-lg border border-[#DCD7CF] bg-white pl-3 pr-9 text-sm"
            >
              <option value="">Усі статуси</option>
              {Object.entries(statusLabels).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
            <ChevronDown
              size={15}
              className="pointer-events-none absolute right-3 top-3 text-[#8A837A]"
            />
          </div>
          <span className="text-sm text-[#817A72]">
            Знайдено: {applications.length}
          </span>
        </div>

        {loading ? (
          <div className="rounded-xl border border-[#E3DED6] bg-white p-8">
            Завантажуємо заявки...
          </div>
        ) : (
          <div className="space-y-4">
            {applications.map((application) => {
              const user =
                typeof application.userId === "string"
                  ? null
                  : application.userId;
              const isApproved = application.status === "approved";

              return (
                <article
                  key={application._id}
                  className="rounded-xl border border-[#E3DED6] bg-white p-4 sm:p-5"
                >
                  <div className="grid gap-5 xl:grid-cols-[minmax(240px,0.8fr)_minmax(320px,1.2fr)_minmax(300px,1fr)]">
                    <div>
                      <div className="flex items-center gap-3">
                        <div className="grid h-11 w-11 place-items-center rounded-lg bg-[#F2B544] font-bold text-[#3E2B08]">
                          {(user?.username || "К")
                            .split(" ")
                            .map((part) => part[0])
                            .join("")
                            .slice(0, 2)}
                        </div>
                        <div>
                          <h3 className="font-bold text-[#312D28]">
                            {user?.username || "Кандидат"}
                          </h3>
                          <p className="text-sm text-[#817A72]">
                            {user?.telephone}
                          </p>
                        </div>
                      </div>
                      <span className="mt-4 inline-flex rounded-full bg-[#F7F4EF] px-3 py-1 text-xs font-semibold text-[#625C55]">
                        {statusLabels[application.status]}
                      </span>
                      <p className="mt-3 text-xs text-[#918A82]">
                        {application.form.city} · {application.form.age} років
                      </p>
                    </div>

                    <div className="grid gap-3 text-sm sm:grid-cols-2">
                      <div>
                        <p className="text-xs font-semibold text-[#918A82]">
                          Мотивація
                        </p>
                        <p className="mt-1 line-clamp-3 leading-5 text-[#514C46]">
                          {application.form.motivation}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-[#918A82]">
                          Ціль на 3 місяці
                        </p>
                        <p className="mt-1 line-clamp-3 leading-5 text-[#514C46]">
                          {application.form.threeMonthGoal}
                        </p>
                      </div>
                    </div>

                    <div className="space-y-3">
                      <textarea
                        rows={2}
                        placeholder="Коментар адміністратора"
                        value={comments[application._id] || ""}
                        onChange={(event) =>
                          setComments({
                            ...comments,
                            [application._id]: event.target.value,
                          })
                        }
                        disabled={isApproved}
                        className="w-full rounded-lg border border-[#DCD7CF] px-3 py-2 text-sm"
                      />

                      {!isApproved ? (
                        <>
                          <div className="flex min-w-0 gap-2">
                            <select
                              className="h-10 min-w-0 flex-1 rounded-lg border border-[#DCD7CF] px-3 text-sm"
                              defaultValue={application.status}
                              onChange={(event) =>
                                changeStatus(
                                  application,
                                  event.target.value as Exclude<
                                    ApplicationStatus,
                                    "approved"
                                  >
                                )
                              }
                              disabled={busyId === application._id}
                            >
                              {editableStatuses.map((status) => (
                                <option key={status} value={status}>
                                  {statusLabels[status]}
                                </option>
                              ))}
                            </select>
                          </div>
                          <div className="flex flex-col gap-2 sm:flex-row">
                            <select
                              value={selectedCohorts[application._id] || ""}
                              onChange={(event) =>
                                setSelectedCohorts({
                                  ...selectedCohorts,
                                  [application._id]: event.target.value,
                                })
                              }
                              className="h-10 min-w-0 flex-1 rounded-lg border border-[#DCD7CF] px-3 text-sm"
                            >
                              <option value="">Оберіть потік</option>
                              {openCohorts.map((cohort) => (
                                <option key={cohort._id} value={cohort._id}>
                                  {cohort.name} ({cohort.participantIds?.length || 0}/
                                  {cohort.capacity})
                                </option>
                              ))}
                            </select>
                            <button
                              type="button"
                              onClick={() => approve(application)}
                              disabled={busyId === application._id}
                              className="inline-flex h-10 w-full items-center justify-center gap-2 rounded-lg bg-[#28713C] px-4 text-sm font-semibold text-white disabled:opacity-50 sm:w-auto"
                            >
                              <UserCheck size={16} />
                              Зарахувати
                            </button>
                          </div>
                        </>
                      ) : (
                        <div className="flex items-center gap-2 rounded-lg bg-[#F0F8F2] px-3 py-3 text-sm font-semibold text-[#276537]">
                          <CheckCircle2 size={17} />
                          Кандидата зараховано
                        </div>
                      )}
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
};

export default AdminApplicationsPage;
